// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { css } from '@emotion/react';
import { awSignUpPalette } from 'Utils/themes';

// Figma signup-flow spec: one column below this width, form beside the pricing card from it
export const AUTH_TWO_COLUMN_MIN_WIDTH_PX = 768;
// Height of the logo row in that spec (an 18px logomark beside a 19px wordmark)
const LOGO_ROW_HEIGHT_PX = 22;
// Width of the signup panel in the spec's "Tablet & Desktop — 768px+" board
const SIGN_UP_PANEL_MAX_WIDTH_PX = 834;
// Side gutters of the spec's mobile boards
const MOBILE_GUTTER_PX = 24;
// Google's reCAPTCHA widget is a fixed 304px wide; narrower screens than this cannot hold it
// inside the spec's gutters, so they get tighter ones rather than sideways scrolling
const RECAPTCHA_WIDTH_PX = 304;
const NARROW_PHONE_MAX_WIDTH_PX = RECAPTCHA_WIDTH_PX + 2 * MOBILE_GUTTER_PX;

export const mediaStyles = {
  video: css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0.5;

    @media (min-aspect-ratio: 16/9) {
      width: 100%;
      height: auto;
    }

    @media (max-aspect-ratio: 16/9) {
      width: auto;
      height: 100%;
    }
  `,
};

const styles = (theme: any) => ({
  videoWrapper: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    overflow: 'hidden',
    zIndex: -100,
    background: 'linear-gradient(134.72deg, #000000 0%, #05071A 49.93%, #18216D 100%)',
  },
  gridWrapper: {
    position: 'relative',
    height: '100%',
  },
  logo: {
    position: 'absolute',
    zIndex: 1,
    [theme.breakpoints.up(AUTH_TWO_COLUMN_MIN_WIDTH_PX)]: {
      margin: theme.spacing(10),
      width: '17%',
    },
    // Phones: the logo row sits inside the panel's top padding, per the spec's mobile boards
    [theme.breakpoints.down(AUTH_TWO_COLUMN_MIN_WIDTH_PX)]: {
      top: theme.spacing(4),
      left: `${MOBILE_GUTTER_PX}px`,
      height: `${LOGO_ROW_HEIGHT_PX}px`,
    },
    [theme.breakpoints.down(NARROW_PHONE_MAX_WIDTH_PX)]: {
      left: theme.spacing(2),
    },
  },
  // Signup flow: the logo row keeps the spec's size and sits inside the panel's 48px desktop padding
  signUpLogo: {
    [theme.breakpoints.up(AUTH_TWO_COLUMN_MIN_WIDTH_PX)]: {
      margin: theme.spacing(6),
      width: 'auto',
      height: `${LOGO_ROW_HEIGHT_PX}px`,
    },
  },
  grid: {
    [theme.breakpoints.up('md')]: {
      height: '100%',
    },
    flexGrow: 1,
  },
  pageDescriptionWrapper: {
    [theme.breakpoints.up('md')]: {
      paddingLeft: theme.spacing(10),
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      paddingTop: theme.spacing(1) * 12,
      paddingBottom: theme.spacing(8),
    },
  },
  pageDescription: {
    [theme.breakpoints.down('sm')]: {
      textAlign: 'center',
    },
  },
  pageDescriptionText: {
    fontFamily: 'Work Sans',
    fontWeight: 300,
    fontSize: '42px',
    lineHeight: '52px',
    textShadow: '1px 1px 12px rgba(0, 0, 0, 0.6)',
    color: 'white',
  },
  form: {
    display: 'block', // Fix IE 11 issue.
    background: '#021422',
    border: 'white',
    [theme.breakpoints.up(AUTH_TWO_COLUMN_MIN_WIDTH_PX)]: {
      position: 'absolute',
      top: 0,
      left: 0,
      minHeight: '100%',
      maxWidth: '650px',
      padding: theme.spacing(12),
    },
    // Phones: the panel flows with the page at full width and at least viewport height (the spec's
    // boards are full-bleed dark), its top padding clearing the logo row
    [theme.breakpoints.down(AUTH_TWO_COLUMN_MIN_WIDTH_PX)]: {
      position: 'relative',
      minHeight: '100vh',
      padding: `calc(${theme.spacing(7)} + ${LOGO_ROW_HEIGHT_PX}px) ${MOBILE_GUTTER_PX}px ${theme.spacing(5)}`,
    },
    [theme.breakpoints.down(NARROW_PHONE_MAX_WIDTH_PX)]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  },
  // Signup flow: the spec's page ground and the wider panel that holds the form beside the pricing card
  signUpForm: {
    background: awSignUpPalette.background,
    [theme.breakpoints.up(AUTH_TWO_COLUMN_MIN_WIDTH_PX)]: {
      width: '100%',
      maxWidth: `${SIGN_UP_PANEL_MAX_WIDTH_PX}px`,
      padding: theme.spacing(6),
      paddingTop: `calc(${theme.spacing(10)} + ${LOGO_ROW_HEIGHT_PX}px)`,
    },
  },
  tabLabel: {
    color: 'white',
  },
  stripeGradient: {
    lineHeight: '8px',
    background: 'linear-gradient(90deg, #9038F5 0%, #3C34B7 33.29%, #2075FE 74.06%, #01A1DD 100%)',
    [theme.breakpoints.up('md')]: {
      maxWidth: '650px',
    },
    [theme.breakpoints.down(AUTH_TWO_COLUMN_MIN_WIDTH_PX)]: {
      display: 'none',
    },
  },
  displayChild: {
    [theme.breakpoints.up(AUTH_TWO_COLUMN_MIN_WIDTH_PX)]: {
      paddingTop: theme.spacing(6),
    },
  },
  // Signup flow: the panel's own top padding already clears the logo row
  signUpDisplayChild: {
    [theme.breakpoints.up(AUTH_TWO_COLUMN_MIN_WIDTH_PX)]: {
      paddingTop: 0,
    },
  },
});

export default styles;
